// Commitment Kanban board — FE-042 / Polish A1.
//
// Horizontal scroller that groups Commitment rows by ``status`` into
// the columns the page chose to render. Polish chunk A1 makes the
// column set caller-driven: the page passes ``visibleStatuses``
// (default: 5 open lanes; ``cancelled`` opt-in via
// ``include_cancelled=true`` or an explicit ``status=cancelled``).
// Sorting *inside* a column is local and deterministic — the BE-052
// list endpoint returns rows in the caller's chosen sort order, but
// on the board we always want overdue items at the top regardless
// of how the user sorted the list.

import { useMemo } from "react";

import { CommitmentKanbanColumn } from "./CommitmentKanbanColumn";
import { DEFAULT_BOARD_STATUSES } from "./commitmentStatusMeta";
import type { GroupedFetchInputs } from "./CommitmentListTable";
import type {
  CommitmentListRow,
  CommitmentStatus,
  CommitmentStatusCounts,
} from "./types";


export interface CommitmentKanbanBoardProps {
  rows: CommitmentListRow[];
  /** Per-status totals across the entire filtered set (independent
   * of ``rows`` which is the visible page slice). Surfaced by the
   * BE list endpoint. Optional so existing tests that mount the
   * board with just ``rows`` keep working — production always passes
   * the full counts. */
  statusCounts?: CommitmentStatusCounts;
  /** When set, each column owns its data via
   * ``useCommitmentBucketInfiniteQuery`` and lazy-loads more rows
   * on column scroll. Mirrors the list view's per-group infinite
   * scroll. Without this, the board capped at the page-level
   * ``rows`` slice and ran out of cards mid-scroll on big trackers
   * (the user reported "auto scrolling stops after a certain
   * amount"). */
  bucketFetch?: GroupedFetchInputs;
  /** Caller-controlled column set. Defaults to the open 5 lanes
   * (Polish A1) so a missing prop never accidentally surfaces
   * Cancelled. */
  visibleStatuses?: CommitmentStatus[];
  /** Page-level callback fired when the user drops a card on a new
   * column or picks a target via the kebab menu. The page wires
   * this to the kanban transition mutation (with optimistic update
   * and the cancellation-reason flow). When omitted, cards render
   * without movement affordances — useful for the existing kanban
   * tests and for any future read-only embed. */
  onMoveCard?: (commitmentId: string, toStatus: CommitmentStatus) => void;
  /** Inline assignee change from a kanban card. */
  onAssignCard?: (commitmentId: string, userId: string | null) => void;
  /** Inline due-date change from a kanban card. */
  onSetCardDueDate?: (commitmentId: string, isoOrNull: string | null) => void;
  /** Members feeding the inline assignee picker on each card. */
  members?: import("./api").MemberPickerRow[];
  /** Id of the card whose move is currently in flight. The
   * matching card disables its menu + drag gesture so the user
   * doesn't fire a second mutation on top of a pending one. */
  pendingCommitmentId?: string | null;
  /** Stamp every card's detail link with router location state so
   * the back link on the detail page returns to this URL. */
  buildLinkState?: () => unknown;
}


export function CommitmentKanbanBoard({
  rows, statusCounts, bucketFetch, visibleStatuses, onMoveCard,
  onAssignCard, onSetCardDueDate, members, pendingCommitmentId,
  buildLinkState,
}: CommitmentKanbanBoardProps) {
  const columns = visibleStatuses ?? DEFAULT_BOARD_STATUSES;
  // Legacy path: page hands the board its pre-fetched ``rows``; we
  // group + sort client-side. New path (``bucketFetch`` supplied):
  // each column self-fetches via its bucket query, so the board's
  // ``rows`` prop is ignored and ``grouped`` is irrelevant.
  const grouped = useMemo(
    () => bucketFetch ? null : groupByStatus(rows, columns),
    [bucketFetch, rows, columns],
  );
  return (
    <div
      data-testid="commitments-board"
      role="region"
      aria-label="Commitment Tracker board"
      // ``flex-1 min-h-0`` makes the board fill the remaining
      // vertical space; each column is ``h-full`` and scrolls its
      // card list independently.
      className="flex min-h-0 min-w-0 max-w-full flex-1 gap-2 overflow-x-auto pb-2"
    >
      {columns.map((status) => (
        <CommitmentKanbanColumn
          key={status}
          status={status}
          rows={grouped ? grouped[status] : []}
          bucketFetch={bucketFetch}
          totalForStatus={statusCounts?.[status]}
          onMove={onMoveCard}
          onAssign={onAssignCard}
          onSetDueDate={onSetCardDueDate}
          members={members}
          visibleStatuses={columns}
          pendingCommitmentId={pendingCommitmentId ?? null}
          buildLinkState={buildLinkState}
        />
      ))}
    </div>
  );
}


function groupByStatus(
  rows: CommitmentListRow[],
  columns: CommitmentStatus[],
): Record<CommitmentStatus, CommitmentListRow[]> {
  const out = columns.reduce<
    Record<CommitmentStatus, CommitmentListRow[]>
  >(
    (acc, status) => {
      acc[status] = [];
      return acc;
    },
    {} as Record<CommitmentStatus, CommitmentListRow[]>,
  );
  for (const row of rows) {
    if (!out[row.status]) continue;
    out[row.status].push(row);
  }
  for (const status of columns) {
    out[status].sort(compareCards);
  }
  return out;
}


// Local column sort: overdue first, then due date ascending (rows
// without a due date go after dated ones), then priority high → low,
// then ``updated_at`` desc as a tiebreaker. Deterministic so the
// snapshot tests can pin order without reaching for ``Date.now``.
const PRIORITY_RANK: Record<string, number> = {
  high: 0, medium: 1, low: 2,
};

function compareCards(a: CommitmentListRow, b: CommitmentListRow): number {
  const aOverdue = isOverdue(a) ? 0 : 1;
  const bOverdue = isOverdue(b) ? 0 : 1;
  if (aOverdue !== bOverdue) return aOverdue - bOverdue;

  const aDue = a.due_date_time ? new Date(a.due_date_time).getTime() : null;
  const bDue = b.due_date_time ? new Date(b.due_date_time).getTime() : null;
  if (aDue != null && bDue != null && aDue !== bDue) return aDue - bDue;
  if (aDue != null && bDue == null) return -1;
  if (aDue == null && bDue != null) return 1;

  const aPri = PRIORITY_RANK[a.priority] ?? 9;
  const bPri = PRIORITY_RANK[b.priority] ?? 9;
  if (aPri !== bPri) return aPri - bPri;

  const aUpd = a.updated_at ? new Date(a.updated_at).getTime() : 0;
  const bUpd = b.updated_at ? new Date(b.updated_at).getTime() : 0;
  return bUpd - aUpd;
}


function isOverdue(row: CommitmentListRow): boolean {
  if (!row.due_date_time) return false;
  if (row.status === "done" || row.status === "cancelled") return false;
  const due = new Date(row.due_date_time).getTime();
  if (Number.isNaN(due)) return false;
  return due < Date.now();
}
